// src/pages/Programs.jsx
import React, { useEffect, useState } from "react";
import {
  getPrograms,
  createProgram,
  updateProgram,
} from "../api/programService";
import { getDepartments } from "../api/departmentService";

import "../styles/program.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [editingProgram, setEditingProgram] = useState(null);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    department_id: "",
  });

  useEffect(() => {
    loadDepartments();
    loadPrograms();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await getDepartments({ fields: "id,name" });
      setDepartments(data);
    } catch (err) {
      console.error("Failed to load departments:", err);
      toast.error("❌ Failed to load departments");
    }
  };

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const data = await getPrograms({ fields: "id,name,code,department_id" });
      setPrograms(data);
    } catch (err) {
      console.error("Failed to load programs:", err);
      toast.error("❌ Failed to load programs");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProgram = async () => {
    if (!formData.name || !formData.code || !formData.department_id) {
      toast.warning("⚠️ Please fill in all fields before saving");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        department_id: parseInt(formData.department_id),
      };

      if (editingProgram) {
        await updateProgram(editingProgram.id, payload);
        toast.success("✅ Program updated successfully!");
        setEditingProgram(null);
      } else {
        await createProgram(payload);
        toast.success("✅ Program created successfully!");
      }

      setFormData({ name: "", code: "", department_id: "" });
      loadPrograms();
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to save program");
    } finally {
      setSaving(false);
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      code: program.code,
      department_id:
        program.department_id?.[0] || program.department_id || "",
    });
  };

  return (
    <div className="programs-container">
      {/* Header */}
      <div className="section-card">
        <h2>📘 Program Management</h2>
      </div>

      {/* Add/Edit Form */}
      <div className="section-card">
        {editingProgram && (
          <div className="editing-banner">
            ✏️ Editing Program: <strong>{editingProgram.name}</strong>
          </div>
        )}

        <h3>{editingProgram ? "✏️ Edit Program" : "➕ Add Program"}</h3>

        <div className="program-form">
          <input
            type="text"
            placeholder="Program Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />

          <input
            type="text"
            placeholder="Program Code"
            value={formData.code}
            onChange={(e) => handleChange("code", e.target.value)}
          />

          <select
            value={formData.department_id}
            onChange={(e) => handleChange("department_id", e.target.value)}
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        <button
          className="animated-save-btn"
          onClick={handleSaveProgram}
          disabled={
            saving ||
            !formData.name ||
            !formData.code ||
            !formData.department_id
          }
        >
          {saving ? (
            <span className="loader"></span>
          ) : editingProgram ? (
            "🔄 Update Program"
          ) : (
            "💾 Save Program"
          )}
        </button>
      </div>

      {/* Programs Table */}
      <div className="section-card">
        <div className="unit-header">
          <h3>📋 All Programs ({programs.length})</h3>

          <input
            type="text"
            placeholder="🔍 Search Program by name or code..."
            className="full-search-input"
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
          />
        </div>

        {/* Loader */}
        {loading ? (
          <div className="loader-wrapper">
            <div className="spinner"></div>
            <p className="loader-text">Loading programs...</p>
          </div>
        ) : (
          <table className="programs-table">
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Program Name</th>
                <th>Code</th>
                <th>Department</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {programs
                .filter(
                  (p) =>
                    p.name.toLowerCase().includes(search) ||
                    p.code.toLowerCase().includes(search)
                )
                .map((prog, idx) => (
                  <tr key={prog.id}>
                    <td>{idx + 1}</td>
                    <td>{prog.id}</td>
                    <td>{prog.name}</td>
                    <td>{prog.code}</td>
                    <td>
                      {prog.department_id?.[1] ||
                        departments.find((d) => d.id === prog.department_id)
                          ?.name ||
                        "N/A"}
                    </td>
                    <td className="table-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditProgram(prog)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default Programs;
